/**
 * Editorial approval, on the document it is about — §7.6 beside §7.4.
 *
 * This is the card the document screen mounts. It exists because the publish
 * button is now refusable: 1b withdraws §7.4's direct `draft → published` edge,
 * so a document that has never been through approval cannot publish, and the
 * only thing worse than that refusal is a refusal that first appears inside the
 * publish dialog. The gate is rendered here, in the open, with the action that
 * clears it.
 *
 * It reads the same `GET /api/approvals/documents/:id` the publish confirmation
 * reads, so the sentence on this card and the sentence in the dialog are one
 * string from one computation. When they could differ, they are the same bug.
 *
 * History is shown rather than only the current request. §7.6's record is
 * "immutable" and unbounded on purpose — the partial unique index covers only
 * the open states, so a document goes through review repeatedly — and a card
 * that showed only the newest request would quietly hide the rejection that the
 * current request exists to supersede.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useDocumentApprovals } from '../../api/queries.ts';
import type { ApprovalRequest } from '../../api/types.ts';
import { formatDateTime } from '../../components/format.ts';
import { Button } from '../../components/ui/button.tsx';
import { ErrorState, Loading } from '../../components/ui/feedback.tsx';
import { Card, CardHeader } from '../../components/ui/layout.tsx';
import { OpenApprovalDialog } from './open.tsx';
import { ReviewerTally } from './reviewers.tsx';
import { ApprovalStatusBadge, blockHeadline } from './vocabulary.tsx';

function RequestLine({ request, current }: { request: ApprovalRequest; current: boolean }) {
  return (
    <li className="flex flex-col gap-1 border-t border-secondary px-5 py-3 first:border-t-0">
      <div className="flex flex-wrap items-center gap-2">
        <ApprovalStatusBadge status={request.status} />
        <span className="text-xs text-tertiary">
          opened {formatDateTime(request.createdAt)}
          {request.resolvedAt ? ` · closed ${formatDateTime(request.resolvedAt)}` : ''}
        </span>
        <Link
          to="/approvals/$requestId"
          params={{ requestId: request.id }}
          className="ml-auto text-xs link"
        >
          {current ? 'Open the request' : 'Read the record'}
        </Link>
      </div>
      {current ? <ReviewerTally reviewers={request.reviewers} /> : null}
    </li>
  );
}

export interface ApprovalCardProps {
  nodeId: string;
  title: string;
  ownerName?: string | null;
  /**
   * Whether to offer the request button. Opening one is `document.edit` — the
   * step before publishing, so the contractor who holds edit and not publish
   * (§10.3) is exactly who needs it — and the server is what enforces that. This
   * flag only decides whether a button that would 403 is worth drawing.
   */
  canRequest?: boolean;
}

export function ApprovalCard({ nodeId, title, ownerName, canRequest = false }: ApprovalCardProps) {
  const approvals = useDocumentApprovals(nodeId);
  const [asking, setAsking] = useState(false);

  const gate = approvals.data?.gate;
  const requests = approvals.data?.requests ?? [];
  const openRequest = requests.find((request) => request.open);

  return (
    <>
      <Card padded={false}>
        <CardHeader
          title="Editorial approval"
          description={
            gate ? (
              <span className={gate.mayPublish ? undefined : 'text-primary'}>
                {blockHeadline(gate.blockedBy)}
              </span>
            ) : (
              '§7.6 — publishing waits on every required reviewer.'
            )
          }
          actions={
            canRequest && !openRequest ? (
              <Button variant="secondary" onPress={() => setAsking(true)}>
                Ask for review
              </Button>
            ) : null
          }
        />

        <div className="flex flex-col gap-3 px-5 py-4">
          {approvals.isPending ? <Loading label="Reading the approval gate…" /> : null}
          {approvals.isError ? <ErrorState error={approvals.error} /> : null}
          {/* The server's sentence, verbatim. It is also the body of publish's
              409, and two wordings for one refusal is how a screen stops being
              believed. */}
          {gate ? <p className="text-sm text-secondary">{gate.explanation}</p> : null}
          {openRequest && canRequest ? (
            <p className="text-xs text-tertiary">
              One approval request is open at a time. Withdraw it from the request screen if it has
              stopped moving — that is the only way to open another.
            </p>
          ) : null}
        </div>

        {requests.length > 0 ? (
          <ul className="flex flex-col border-t border-secondary">
            {requests.map((request) => (
              <RequestLine
                key={request.id}
                request={request}
                current={request.id === gate?.requestId}
              />
            ))}
          </ul>
        ) : null}
      </Card>

      <OpenApprovalDialog
        nodeId={nodeId}
        title={title}
        ownerName={ownerName ?? null}
        isOpen={asking}
        onOpenChange={setAsking}
      />
    </>
  );
}
